import commissionsApi from '@/api/commissions'

const getDefaultState = () => ({
  cardExpirationMonth: '',
  cardExpirationYear: '',
  cardHolderName: '',
  cardHolderFirstName: '',
  cardHolderLastName: '',
  cardHolderEmail: '',
  cardHolderStreet: '',
  cardHolderCity: '',
  cardHolderState: '',
  cardHolderCountry: '',
  cardHolderPhone: '',
  cardHolderZipCode: '',
  cardNumber: '',
  cardType: 0,
  commission: {},
  commissionType: '',
  commissionAmount: '',
  commissionBrand: '',
  newBalance: 0,
  selectedVehicleId: 0
})

const state = getDefaultState()

const mutations = {
  CLEAR_STATE: (state, value) => {
    Object.assign(state, getDefaultState())
  },
  SET_CARD_EXPIRATION_MONTH: (state, value) => {
    state.cardExpirationMonth = value
  },
  SET_CARD_EXPIRATION_YEAR: (state, value) => {
    state.cardExpirationYear = value
  },
  SET_CARD_HOLDER_NAME: (state, value) => {
    state.cardHolderName = value
  },
  SET_CARD_HOLDER_FIRST_NAME: (state, value) => {
    state.cardHolderFirstName = value
  },
  SET_CARD_HOLDER_LAST_NAME: (state, value) => {
    state.cardHolderLastName = value
  },
  SET_CARD_HOLDER_EMAIL: (state, value) => {
    state.cardHolderEmail = value
  },
  SET_CARD_HOLDER_PHONE: (state, value) => {
    state.cardHolderPhone = value.replace(/\D/g, '')
  },
  SET_CARD_HOLDER_STREET: (state, value) => {
    state.cardHolderStreet = value
  },
  SET_CARD_HOLDER_CITY: (state, value) => {
    state.cardHolderCity = value
  },
  SET_CARD_HOLDER_STATE: (state, value) => {
    state.cardHolderState = value
  },
  SET_CARD_HOLDER_COUNTRY: (state, value) => {
    state.cardHolderCountry = value
  },
  SET_CARD_HOLDER_ZIPCODE: (state, value) => {
    state.cardHolderZipCode = value
  },
  SET_CARD_NUMBER: (state, value) => {
    state.cardNumber = value
  },
  SET_CARD_TYPE: (state, value) => {
    state.cardType = value
  },
  SET_COMMISSION: (state, value) => {
    state.commissionType = value.type
    state.commissionAmount = value.amount
    state.commissionBrand = value.brand
  },
  SET_NEW_BALANCE: (state, value) => {
    state.newBalance = value
  },
  SET_SELECTED_VEHICLE_ID: (state, value) => {
    state.selectedVehicleId = value
  }
}

const actions = {
  clearDraftSaleState ({ commit }) {
    commit('CLEAR_STATE')
  },
  resetCommission ({ commit }) {
    commit('SET_COMMISSION', getDefaultState().commission)
  },
  setSelectedVehicleId ({ commit }, value) {
    commit('SET_SELECTED_VEHICLE_ID', parseInt(value))
  },
  updateCardExpirationMonth ({ commit }, value) {
    commit('SET_CARD_EXPIRATION_MONTH', value)
  },
  updateCardExpirationYear ({ commit }, value) {
    commit('SET_CARD_EXPIRATION_YEAR', value)
  },
  updateCardHolderName ({ commit }, value) {
    commit('SET_CARD_HOLDER_NAME', value)
  },
  updateCardHolderFirstName ({ commit }, value) {
    commit('SET_CARD_HOLDER_FIRST_NAME', value)
  },
  updateCardHolderLastName ({ commit }, value) {
    commit('SET_CARD_HOLDER_LAST_NAME', value)
  },
  updateCardHolderEmail ({ commit }, value) {
    commit('SET_CARD_HOLDER_EMAIL', value)
  },
  updateCardHolderPhone ({ commit }, value) {
    commit('SET_CARD_HOLDER_PHONE', value.replace(/\D/g, ''))
  },
  updateCardHolderStreet ({ commit }, value) {
    commit('SET_CARD_HOLDER_STREET', value)
  },
  updateCardHolderCity ({ commit }, value) {
    commit('SET_CARD_HOLDER_CITY', value)
  },
  updateCardHolderZipCode ({ commit }, value) {
    commit('SET_CARD_HOLDER_ZIPCODE', value)
  },
  updateCardHolderState ({ commit }, value) {
    commit('SET_CARD_HOLDER_STATE', value)
  },
  updateCardHolderCountry ({ commit }, value) {
    commit('SET_CARD_HOLDER_COUNTRY', value)
  },
  updateCardNumber ({ commit, dispatch, state }, value) {
    commit('SET_CARD_NUMBER', value)
  },
  updateCardType ({ commit }, value) {
    commit('SET_CARD_TYPE', value)
  },
  async getCommission ({ commit, state }) {
    try {
      const { data } = await commissionsApi.getCardCommission(state.cardNumber)
      const { amount, brand, type } = data

      commit('SET_COMMISSION', {
        amount: parseFloat(amount),
        brand,
        type
      })
    } catch (error) {
    }
  },
  updateNewBalance ({ commit, getters, rootGetters }) {
    const product = rootGetters['products/selectedProduct']
    const selectedVehicle = getters.selectedVehicle
    const newBalance = selectedVehicle.balance + parseInt(product.price)

    commit('SET_NEW_BALANCE', newBalance)
  }
}

const getters = {
  cardBrand: (state) => {
    if (state.commissionBrand === 'MASTER CARD') {
      return 'MC'
    }

    return state.commissionBrand
  },
  isInternational: (state) => {
    return state.commissionType === 'international'
  },
  cardHolderName: (state) => {
    return `${state.cardHolderFirstName} ${state.cardHolderLastName}`
  },
  creditType: (state) => {
    return (state.cardType === 0) ? 'DB' : 'CR'
  },
  cardExpirationDate: (state) => {
    return `${state.cardExpirationMonth}/${state.cardExpirationYear.slice(-2)}`
  },
  commissionPrice: (state, getters, rootState, rootGetters) => {
    const commissionAmount = state.commissionAmount
    const selectedProduct = rootGetters['products/selectedProduct']

    if (commissionAmount > 0 && selectedProduct) {
      const valueBeforeTax = parseFloat(selectedProduct.price) * parseFloat(commissionAmount)
      const commissionPrice = valueBeforeTax + (valueBeforeTax * 0.16)

      return commissionPrice
    }

    return null
  },
  formattedCommissionPrice: (state, getters) => {
    const commissionPrice = getters.commissionPrice

    if (commissionPrice) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'MXN',
        currencyDisplay: 'symbol'
      }).format(commissionPrice)
    }

    return null
  },
  formattedNewBalance: state => {
    if (state.newBalance > 0) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'MXN',
        currencyDisplay: 'symbol'
      }).format(state.newBalance)
    }

    return null
  },
  formattedTotal: (state, getters, rootState, rootGetters) => {
    const selectedProduct = rootGetters['products/selectedProduct']
    const commissionPrice = getters.commissionPrice

    if (selectedProduct && commissionPrice) {
      const total = parseFloat(selectedProduct.price) + parseFloat(commissionPrice)

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'MXN',
        currencyDisplay: 'symbol'
      }).format(total)
    }

    return null
  },
  selectedVehicle: (state, getters, rootState, rootGetters) => {
    return rootGetters['vehicles/findVehicle'](state.selectedVehicleId)
  },
  selectedVehicleCanRenovateAnnuality: (state, getters) => {
    if (getters.selectedVehicle.contract_type === 'Saldo') {
      return false
    }

    const today = new Date()
    const expirationDate = new Date(getters.selectedVehicle.expiration_date)

    const availableRenovationDate = new Date(
      expirationDate.getFullYear(),
      expirationDate.getMonth(),
      expirationDate.getDate() - 90
    )

    return availableRenovationDate < today
  },
  selectedVehicleNewExpirationDate: (state, getters, rootState, rootGetters) => {
    return rootGetters['vehicles/vehicleNewExpirationDate'](state.selectedVehicleId)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
